import OpenInNew from '@mui/icons-material/OpenInNew'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import i18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import { memo, Fragment } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router'

import { awaitActionType } from '../../lib/awaitActionTypeMiddleware'
import ConfirmDialog from '../../lib/ConfirmDialog'
import useModalState from '../../lib/useModalState'
import { currentRetailerAccountSelector } from '../../stores/app/selectors'
import * as actions from '../../stores/automations/actions'
import { getAll } from '../../stores/automations/selectors'
import {
  findRetailerForBrand,
  findAutomation,
} from '../../stores/automations/util'
import { facebookPagesById } from '../../stores/facebookPages/selectors'
import useTracking from '../shared/Tracking/useTracking'

/**
 * @typedef {{
 *  facebookPageId: string | number
 *  brandId: string | number
 * }} AutomationBrandFacebookPageCheckboxProps
 */

/** @type {React.FC<AutomationBrandFacebookPageCheckboxProps>} */
const AutomationBrandFacebookPageCheckbox = (props) => {
  const match = useRouteMatch()
  const { enqueueSnackbar } = useSnackbar()
  const { isOpen, closeModal, openModal } = useModalState()
  const { facebookPageId, brandId } = props

  let automation = undefined
  let retailer = undefined
  let isAutomationEnabled = false
  let doesAutomationBelongToFacebookPage = false

  const dispatch = useDispatch()
  const automations = useSelector(getAll)
  const retailerAccount = useSelector(currentRetailerAccountSelector)

  if (retailerAccount) {
    retailer = findRetailerForBrand(retailerAccount, brandId)
    automation = findAutomation(automations, retailer.id, brandId)
  }

  if (automation) {
    isAutomationEnabled = automation.state === 'enabled'
    doesAutomationBelongToFacebookPage =
      String(automation.customization.facebook_page_id) ===
      String(facebookPageId)
  }

  const locations = useSelector((state) => {
    return retailer
      ? (state.locations.retailers[retailer.id] || {}).response || EMPTY_ARRAY
      : EMPTY_ARRAY
  })
  const facebookPages = useSelector(facebookPagesById)
  const { track } = useTracking()

  if (
    !retailerAccount ||
    !retailer ||
    !retailer.brand ||
    !facebookPages[facebookPageId]
  ) {
    return null
  }

  const updateAutomation = (isEnabled) => {
    const state = isEnabled ? 'enabled' : 'disabled'
    const awaiter = awaitActionType({
      resolve: [actions.UPDATE_AUTOMATIONS_FULFILLED],
      reject: [actions.UPDATE_AUTOMATIONS_REJECTED],
    })

    const facebookPage = facebookPages[facebookPageId]
    awaiter.promise
      .then(() => {
        enqueueSnackbar(
          i18n.t(`automations.automationSuccess.${state}`, {
            facebookPageName: facebookPage.name,
          }),
          { variant: 'success' },
        )

        track({
          event: 'fppaToggleAutomation',
          data: {
            facebookPageId,
            state,
            brandId,
            retailerId: retailer.id,
          },
        })
      })
      .catch((err) => {
        console.error(err)
        enqueueSnackbar(i18n.t('automations.automationError'), {
          variant: 'error',
        })
      })

    dispatch(
      actions.updateAutomation({
        ...automation,
        state,
        customization: {
          ...automation.customization,
          facebook_page_id: String(facebookPageId),
        },
      }),
    )
  }

  const createAutomation = (isEnabled) => {
    const state = isEnabled ? 'enabled' : 'disabled'
    const awaiter = awaitActionType({
      resolve: [actions.CREATE_AUTOMATIONS_FULFILLED],
      reject: [actions.CREATE_AUTOMATIONS_REJECTED],
    })

    const facebookPage = facebookPages[facebookPageId]
    awaiter.promise
      .then(() => {
        enqueueSnackbar(
          i18n.t(`automations.automationSuccess.${state}`, {
            facebookPageName: facebookPage.name,
          }),
          { variant: 'success' },
        )
        track({
          event: 'fppaToggleAutomation',
          data: {
            facebookPageId,
            state,
            brandId,
            retailerId: retailer.id,
          },
        })
      })
      .catch((err) => {
        console.error(err)
        enqueueSnackbar(i18n.t('automations.automationError'), {
          variant: 'error',
        })
      })

    dispatch(
      actions.createAutomation({
        brand_id: String(brandId),
        retailer_id: String(retailer.id),
        state,
        channel: 'facebook',
        customization: {
          channel: 'facebook',
          facebook_page_id: String(facebookPageId),
          geo_locations: {
            custom_locations: locations.map((location) => ({
              latitude: parseFloat(location.latitude),
              longitude: parseFloat(location.longitude),
              radius: 15,
            })),
          },
        },
      }),
    )
  }

  const handleAutomationCheckbox = (_event, isEnabled) => {
    if (automation) {
      if (isEnabled && isAutomationEnabled) {
        if (doesAutomationBelongToFacebookPage) {
          updateAutomation(isEnabled)
        } else {
          openModal()
        }
      } else {
        updateAutomation(isEnabled)
      }
    } else {
      createAutomation(isEnabled)
    }
  }

  const confirmMessage = () => {
    // Should be impossible at this point, but TS won't know that.
    if (!automation) {
      return
    }

    const oldFacebookPage =
      facebookPages[automation.customization.facebook_page_id]

    if (!oldFacebookPage) {
      return
    }

    return (
      <Fragment>
        {i18n.t('automations.confirmWillDisable')}{' '}
        <strong>{oldFacebookPage.name}</strong>.
      </Fragment>
    )
  }

  const confirmTitle = () => {
    // Should be impossible at this point, but TS won't know that.
    if (!automation) {
      return
    }

    const facebookPage = facebookPages[facebookPageId]

    if (!facebookPage) {
      return
    }

    return i18n.t('automations.confirmTitle', {
      facebookPageName: facebookPage.name,
    })
  }

  return (
    <Fragment>
      <FormControlLabel
        control={
          <Checkbox
            checked={isAutomationEnabled && doesAutomationBelongToFacebookPage}
            onChange={handleAutomationCheckbox}
          />
        }
        label={i18n.t('automations.automationCheckboxLabel', {
          brandName: retailer.brand.name,
        })}
      />
      <br />
      <Button
        color="secondary"
        endIcon={<OpenInNew fontSize="small" />}
        href={`/#/${match.params.retailerAccountSlug}/all/settings?tab=social_accounts`}
        size="small"
        target="_blank"
        variant="text"
      >
        {i18n.t('automations.viewAll')}
      </Button>
      <ConfirmDialog
        content={confirmMessage()}
        onClose={closeModal}
        onConfirmClick={() => {
          updateAutomation(true)
        }}
        open={isOpen}
        title={confirmTitle()}
      />
    </Fragment>
  )
}

const EMPTY_ARRAY = []

export default memo(AutomationBrandFacebookPageCheckbox)
